import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { authAPI } from "../services/api";
import { PasswordStrength, passwordValid } from "./shared/index";
import { useLanguage } from "../context/LanguageContext";
import { MdVpnKey, MdWarning, MdVisibility, MdVisibilityOff, MdLogout } from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const inputStyle = {
  width:"100%", padding:"9px 36px 9px 12px", border:`1px solid ${BORDER}`,
  borderRadius:8, fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};

function PasswordField({ label, value, onChange, onEnter, placeholder, style }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:"block", fontSize:12, color:MUTED,
                      marginBottom:4, fontFamily:KH }}>{label}</label>
      <div style={{ position:"relative" }}>
        <input type={show ? "text" : "password"} value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => e.key==="Enter" && onEnter && onEnter()}
          placeholder={placeholder} style={style || inputStyle} />
        <button type="button" onClick={() => setShow(s => !s)}
          style={{ position:"absolute", top:"50%", right:10, transform:"translateY(-50%)",
                   background:"none", border:"none", cursor:"pointer", padding:0,
                   display:"flex", alignItems:"center", color:MUTED }}>
          {show ? <MdVisibilityOff size={17} /> : <MdVisibility size={17} />}
        </button>
      </div>
    </div>
  );
}

export default function ForcePasswordModal({ user }) {
  const { completePasswordSetup, pendingPassword, logout } = useAuth();
  const { t } = useLanguage();
  const [form,    setForm]    = useState({ oldPassword:"", newPassword:"", confirm:"" });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  const needsOldPasswordInput = !pendingPassword;

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    const oldPassword = pendingPassword || form.oldPassword;
    if (!oldPassword || !form.newPassword || !form.confirm) {
      setError(t("សូមបំពេញពាក្យសម្ងាត់ទាំងអស់")); return;
    }
    if (form.newPassword !== form.confirm) {
      setError(t("ពាក្យសម្ងាត់ថ្មីមិនត្រូវគ្នា")); return;
    }
    if (!passwordValid(form.newPassword)) {
      setError(t("ពាក្យសម្ងាត់ថ្មីមិនទាន់គ្រប់លក្ខខណ្ឌ")); return;
    }
    setSaving(true); setError("");
    try {
      await authAPI.changePassword(user.id, {
        oldPassword,
        newPassword:     form.newPassword,
        confirmPassword: form.confirm,
      });
      completePasswordSetup();
    } catch (e) {
      setError(e.message || t("ផ្លាស់ប្តូរពាក្យសម្ងាត់បរាជ័យ"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.6)",
      zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center",
    }}>
      <div style={{
        background:"var(--surface)", borderRadius:16, width:440,
        maxWidth:"95vw", boxShadow:"0 24px 80px rgba(0,0,0,0.35)",
      }}>
        <div style={{ background:BROWN, padding:"20px 24px", borderRadius:"16px 16px 0 0",
                      position:"relative" }}>
          <button onClick={logout} title={t("ចាកចេញ")}
            style={{ position:"absolute", top:16, right:16,
                     display:"flex", alignItems:"center", gap:5,
                     background:"rgba(255,255,255,0.15)", border:"none",
                     borderRadius:20, padding:"6px 12px", cursor:"pointer",
                     color:"#fff", fontSize:11.5, fontFamily:KH }}>
            <MdLogout size={13} /> {t("ចាកចេញ")}
          </button>
          <p style={{ fontSize:16, fontWeight:700, color:"#fff", fontFamily:MO, marginBottom:4,
                      paddingRight:70 }}>
            {t("សូមកំណត់ពាក្យសម្ងាត់ថ្មី")}
          </p>
          <p style={{ fontSize:12, color:"rgba(255,255,255,0.75)", fontFamily:KH }}>
            {t("នេះជាការចូលប្រើប្រាស់ដំបូងរបស់អ្នក។ សូមផ្លាស់ប្តូរពាក្យសម្ងាត់ដើម្បីបន្ត។")}
          </p>
        </div>

        <div style={{ padding:24 }}>
          {error && (
            <div style={{
              background:"#FEE2E2", color:"#991B1B",
              padding:"10px 14px", borderRadius:8,
              fontSize:12, marginBottom:14,
              display:"flex", alignItems:"center", gap:7,
              border:"1px solid #FECACA",
            }}>
              <MdWarning size={15} style={{ flexShrink:0 }} />
              {error}
            </div>
          )}

          {needsOldPasswordInput && (
            <PasswordField label={t("ពាក្យសម្ងាត់បច្ចុប្បន្ន")}
              value={form.oldPassword} onChange={v => f("oldPassword", v)}
              onEnter={handleSubmit} placeholder={t("ពាក្យសម្ងាត់បច្ចុប្បន្ន...")} />
          )}

          <PasswordField label={t("ពាក្យសម្ងាត់ថ្មី")}
            value={form.newPassword} onChange={v => f("newPassword", v)}
            onEnter={handleSubmit} placeholder={t("ពាក្យសម្ងាត់ថ្មី...")}
            style={{
              ...inputStyle,
              ...(form.newPassword && !passwordValid(form.newPassword)
                ? { border:"1.5px solid #EF4444", background:"#FFF5F5" }
                : {}),
            }} />
          <PasswordStrength password={form.newPassword} />

          <PasswordField label={t("បញ្ជាក់ពាក្យសម្ងាត់ថ្មី")}
            value={form.confirm} onChange={v => f("confirm", v)}
            onEnter={handleSubmit} placeholder={t("បញ្ជាក់ម្តងទៀត...")} />

          <button onClick={handleSubmit} disabled={saving}
            style={{ width:"100%", padding:"10px", background:BROWN, color:"#fff",
                     border:"none", borderRadius:8,
                     cursor:saving?"not-allowed":"pointer",
                     fontSize:13, fontFamily:KH, opacity:saving?0.7:1,
                     display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                     marginTop:6 }}>
            {saving ? t("កំពុងផ្លាស់ប្តូរ...") : <><MdVpnKey size={15}/> {t("ផ្លាស់ប្តូរពាក្យសម្ងាត់")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
